import { afterNextRender, ChangeDetectorRef, Component, computed, DestroyRef, ElementRef, inject, input, InputSignal, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { retry, tap } from 'rxjs';
import { GiftData, PurchasePayload } from '../../../pages/gift-cards/gift-card.types';
import { GiftCardService } from '../../../pages/gift-cards/gift-cards.service';
import { GlobalRequestStatus } from '../../common/types';
import { unableOperationMessage } from '../../common/utils';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { LocalStorageService } from '../../local-storage.service';
import { NotificationService } from '../../notification/notification.service';
import { AuthorizeService } from '../../purchase/authorize/authorize.service';
import { ANetPayload, ANetResponse } from '../../purchase/authorize/authorize.types';
import { TRANSACTION_ERROR, TRANSACTION_SUCCESSFULLY_SENT } from '../../purchase/purchase.constants';
import { SignalsStoreService } from '../../signals-store.service';
import { ModalContentService } from '../modal-content.service';
import { Session } from '../../types/session.type';
import { SuccessAnimatedIcon } from '../../success-animated-icon/success-animated-icon';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { MatRadioButton, MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CollectJSFieldTypes, CollectJSValidationResult, initialCollectJSValidationResult } from '../../../settings/billing/payment-method/payment-method.types';
import { PaymentMethodService } from '../../../settings/billing/payment-method/payment-method.service';
import { CommonModule } from '@angular/common';
import { MaskCardNumberDirective } from '../../directives/maskCardNumber.directive';
import { MaskDateDirective } from '../../directives/maskDate.directive';
import { OnlyNumberDirective } from '../../directives/only-number.directive';
import { MatSelectModule } from '@angular/material/select';
import { Item } from '../../types/common.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-nmi-purchase',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    MatRadioModule,
    SuccessAnimatedIcon,
    EmptyMessageComponent,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './nmi-purchase.component.html',
  styleUrl: './nmi-purchase.component.scss'
})
export class NmiPurchaseComponent {

  purchaseReqAddFields: InputSignal<any> = input<any>(null);


  #formBuilder = inject(FormBuilder);
  #signalsStoreService = inject(SignalsStoreService);
  #localStorageService = inject(LocalStorageService);
  #activeModal = inject(NgbActiveModal);
  #authorizeService = inject(AuthorizeService);
  #giftCardService = inject(GiftCardService);
  #notificationService = inject(NotificationService);
  #modalContentService = inject(ModalContentService);
  #paymentMethodService = inject(PaymentMethodService);
  private destroyRef = inject(DestroyRef);

  hasSession: Signal<boolean> = computed(() => this.#signalsStoreService.hasSession());
  sessionCardNumber: WritableSignal<string> = signal('');
  formattedSessionCardNumber: Signal<string> = computed(() => this.#formatSessionCardNumber());
  wasRequestTriggered: WritableSignal<boolean> = signal(false);
  shouldDisableButtons: WritableSignal<boolean> = signal(false);
  isResponseFinished: WritableSignal<boolean> = signal(false);
  isResponseSucces: WritableSignal<boolean> = signal(false);
  isResponseError: WritableSignal<boolean> = signal(false);
  responseMessage: WritableSignal<string> = signal('');

  shouldCloseOnPurchaseFinished: InputSignal<boolean> = input(false);
  purchaseSummary: InputSignal<string> = input('');

  purchaseForm = this.#formBuilder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required]
  })

  //#region NMI - CollectJS config:
  #cdr = inject(ChangeDetectorRef);

  // TODO: This is to force interaction with the app for NMI issue. It's necessary to look for a best way to handle this:
  @ViewChild('btnNMIChangesTrigger') btnNMIChangesTrigger!: ElementRef;

  collectJS_ccnumber_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)
  collectJS_ccexp_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)
  collectJS_cvv_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)

  collectJSResponse: WritableSignal<any> = signal(null);

  //#endregion

  states: WritableSignal<Item[]> = signal([]);

  billingAddressForm = this.#formBuilder.group({
    street: ['', [Validators.required]],
    city: ['', [Validators.required]],
    stateCode: ['', [Validators.required]],
    zip: ['', [Validators.required, Validators.maxLength(5), Validators.minLength(3)]]
  })


  constructor() {
    afterNextRender(() => {
      if (this.hasSession()) {
        this.#setUpSessionCreditCardNumber();
      } else {
        this.#getStates();
        this.#paymentMethodService.clearCollectJSResponse();
        this.#subscribeToCollectJsValidationChanges();
        this.#subscribeToCollectJSResponse();
        this.#subscribeToCollectJSTimeout();
        setTimeout(() => {
          this.#paymentMethodService.setUpNMI();
        }, 1);
      }
    })
  }

  #setUpSessionCreditCardNumber() {
    const session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    this.sessionCardNumber.set(session?.creditCard?.number ?? '');
  }

  #formatSessionCardNumber() {
    const sessionCardNumber = this.sessionCardNumber();
    if (!sessionCardNumber) return '';
    return sessionCardNumber.padStart(29, '**** ');
  }

  validateErrors(controlName: string): string | null {
    const control = this.purchaseForm.get(controlName);
    if (control?.valid || (!control?.dirty && !control?.touched)) return null;
    if (control?.hasError('required')) {
      return `This field is required`
    }
    return null;
  }

  validateBillingAddressErrors(controlName: string): string | null {
    const control = this.billingAddressForm.get(controlName);
    if (control?.valid || (!control?.dirty && !control?.touched)) return null;
    if (control?.hasError('required')) {
      return `This field is required`
    } else if (control?.hasError('minlength')) {
      return `This field must have at least ${control?.errors?.['minlength']?.requiredLength} digits.`
    } else if (control?.hasError('maxlength')) {
      return `This field can have a maximum of ${control?.errors?.['maxlength']?.requiredLength} digits.`
    }
    return null;
  }


  #sendPurchaseWithSession() {
    this.wasRequestTriggered.set(true);
    const payload = this.#setUpPayloadWithSession();
    if (!payload) return unableOperationMessage(this.#modalContentService);
    this.#giftCardService.sendPurchase(payload).pipe(
      tap(res => this.#handlePurchaseResponse(res)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  #sendPurchase() {
    this.wasRequestTriggered.set(true);
    this.btnNMIChangesTrigger.nativeElement.click();
    const payload = this.#setUpPayload();
    if (!payload) return this.#notificationService.show({ text: 'Validate mandatory fields', type: 'error' });
    this.#giftCardService.sendPurchase(payload).pipe(
      tap(res => this.#handlePurchaseResponse(res)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

  }

  #handlePurchaseResponse(res: GlobalRequestStatus) {
    this.isResponseFinished.set(res.finished);
    this.isResponseSucces.set(res.success);
    this.isResponseError.set(res.error);
    this.responseMessage.set(res.error ? TRANSACTION_ERROR : TRANSACTION_SUCCESSFULLY_SENT);
    this.shouldDisableButtons.set(false);
    this.btnNMIChangesTrigger.nativeElement.click();
  }

  #setUpPayload(): PurchasePayload | null {
    if (!this.purchaseForm.valid) {
      this.purchaseForm.markAllAsTouched();
      return null;
    }

    const value = this.purchaseForm.value;
    if (!value.firstName || !value.lastName) {
      this.purchaseForm.markAllAsTouched();
      return null;
    }

    const fields = this.purchaseReqAddFields()
    if (!fields) return null;

    const { senderName, senderEmail, recipientName, recipientEmail, variantId, personalMessage } = fields;

    const { street, zip, stateCode, city } = this.billingAddressForm.value;

    return {
      card: {
        firstName: value.firstName,
        lastName: value.lastName,
        number: this.collectJSResponse().card,
        expiration: this.collectJSResponse().exp,
        token: this.collectJSResponse().token
      },
      giftData: {
        senderName,
        senderEmail,
        recipientName,
        recipientEmail,
        personalMessage,
        variantId
      },
      billingAddress: {
        street: street ?? '',
        zip: zip ?? 0,
        stateCode: stateCode ?? '',
        city: city ?? ''
      }
    } as PurchasePayload
  }

  #setUpPayloadWithSession(): PurchasePayload | null {
    const fields = this.purchaseReqAddFields()
    if (!fields) return null;

    const { senderName, senderEmail, recipientName, recipientEmail, variantId, personalMessage } = fields;

    return {
      giftData: {
        senderName,
        senderEmail,
        recipientName,
        recipientEmail,
        personalMessage,
        variantId
      }
    } as PurchasePayload
  }

  closeModal() {
    const redirect = this.isResponseFinished() && this.isResponseSucces();
    this.#activeModal.close({ redirect });
  }

  //#region NMI - CollectJS methods:

  #subscribeToCollectJsValidationChanges() {
    this.#paymentMethodService.collectJSValidation$.pipe(
      tap((result: CollectJSValidationResult) => {
        switch (result.type) {
          case CollectJSFieldTypes.ccnumber:
            this.collectJS_ccnumber_validation.set(result);
            break;
          case CollectJSFieldTypes.ccexp:
            this.collectJS_ccexp_validation.set(result);
            break;
          case CollectJSFieldTypes.cvv:
            this.collectJS_cvv_validation.set(result);
            break;

          default:
            break;
        }

        this.#cdr.detectChanges();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  #subscribeToCollectJSResponse() {
    this.#paymentMethodService.collectJSResponse$.pipe(
      tap((response: any) => {
        if (!response) return;
        this.collectJSResponse.set(response);
        this.#cdr.detectChanges();
        setTimeout(() => {
          this.#sendPurchase();
        }, 1000);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  confirmPurchase() {
    if (!this.hasSession() && !this.billingAddressForm.valid) return this.billingAddressForm.markAllAsTouched();
    this.shouldDisableButtons.set(true);
    this.btnNMIChangesTrigger.nativeElement.click();
    if (this.sessionCardNumber())
      return this.#sendPurchaseWithSession();
  }

  #subscribeToCollectJSTimeout() {
    this.#paymentMethodService.collectJSTimeout$.pipe(
      tap((res: boolean) => {
        if (!res) return;
        this.isResponseFinished.set(true);
        this.isResponseError.set(true);
        this.isResponseSucces.set(false);
        this.responseMessage.set(TRANSACTION_ERROR);
        this.shouldDisableButtons.set(false);
        this.btnNMIChangesTrigger.nativeElement.click();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  #getStates() {
    this.#paymentMethodService.getStates().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(states => this.states.set(states));
  }

  //#endregion

}
